import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BLOGS, getBlog } from "../data/blogs";
import NewsletterCTA from "../components/NewsletterCTA";
import BlogCover from "../components/BlogCover";

function Block({ block }) {
  if (typeof block === "string") {
    return <p className="font-editorial text-xl leading-relaxed md:text-2xl">{block}</p>;
  }
  if (block.type === "h2") {
    return (
      <h2 className="mt-10 font-display text-3xl font-black leading-tight tracking-tight md:text-4xl">
        {block.text}
      </h2>
    );
  }
  if (block.type === "h3") {
    return (
      <h3 className="mt-6 font-display text-2xl font-black tracking-tight text-sun-red">
        {block.text}
      </h3>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="my-10 border-l-4 border-sun-orange bg-sun-yellow/40 p-8 font-editorial text-3xl italic leading-snug md:text-4xl">
        &ldquo;{block.text}&rdquo;
      </blockquote>
    );
  }
  return null;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const blog = getBlog(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!blog) return <Navigate to="/blog" replace />;

  const related = BLOGS.filter((b) => b.slug !== slug).slice(0, 3);
  const isDark = !!blog.darkHeader;
  const headerTextClass = isDark ? "text-cream" : "text-jet";
  const headerDekClass = isDark ? "text-cream/85" : "text-jet";
  const headerLinkClass = isDark ? "text-cream/80 hover:text-butter" : "text-jet hover:text-cobalt";

  return (
    <main data-testid={`page-blog-${blog.slug}`}>
      {/* Header */}
      <section
        className={`relative overflow-hidden border-b-2 border-jet pt-[104px] md:pt-[128px] ${headerTextClass}`}
        style={{ background: blog.accent }}
      >
        <div className="halftone absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-5xl p-8 md:p-14 lg:p-20">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className={`inline-flex items-center gap-2 font-sans text-xs font-extrabold uppercase tracking-[0.22em] ${headerLinkClass}`}
          >
            <ArrowLeft size={14} /> Back to journal
          </Link>
          <p className={`mt-8 font-sans text-xs font-extrabold uppercase tracking-[0.22em] ${isDark ? "text-butter" : "text-jet/70"}`}>
            {blog.tag} · {blog.readTime}
          </p>
          <h1 className={`mt-4 font-sans text-4xl font-extrabold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl ${headerTextClass}`}>
            {blog.title}
          </h1>
          <p className={`mt-6 max-w-3xl font-sans text-lg font-semibold leading-snug md:text-xl ${headerDekClass}`}>
            {blog.dek}
          </p>
        </div>
        <div className="relative mx-auto max-w-5xl px-8 pb-8 md:px-14 lg:px-20">
          <div className="aspect-[21/9] w-full overflow-hidden border-2 border-jet shadow-pop-lg">
            <BlogCover blog={blog} />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-cream p-8 md:p-14 lg:p-20">
        <div className="mx-auto max-w-3xl space-y-5">
          {blog.body.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      </article>

      {/* Newsletter — shared orange section */}
      <NewsletterCTA source={`blog-${blog.slug}`} id="newsletter" />

      {/* Related */}
      <section className="bg-surface p-8 md:p-14 lg:p-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-sans text-3xl font-extrabold leading-tight tracking-tight md:text-5xl">
            Keep reading.
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {related.map((b) => (
              <Link
                key={b.slug}
                to={`/blog/${b.slug}`}
                data-testid={`related-${b.slug}`}
                className="group flex flex-col border-2 border-jet bg-cream shadow-pop transition-all hover:-translate-y-1 hover:shadow-pop-lg"
              >
                <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-jet">
                  <BlogCover blog={b} />
                </div>
                <div className="p-5">
                  <p className="font-sans text-[10px] font-extrabold uppercase tracking-[0.22em] text-cobalt">
                    {b.tag}
                  </p>
                  <h3 className="mt-2 font-sans text-xl font-extrabold leading-tight tracking-tight">
                    {b.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
